import React, { useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import s from './teacher-card.module.scss'
import { useNavigate } from 'react-router-dom'
import { getFullName } from '@utils/lib/getFullname'
import { getNoun } from '@utils/lib/getNoun'
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji'
import { Button } from '@shared/ui/button/button'
import { StartChatButton } from '@features/messanger/start-chat'
import {
  IconStarFilled,
  IconBook,
  IconClockHour3,
  IconDiscount,
  IconStar,
  IconPercentage,
  IconSparkles,
} from '@tabler/icons-react'
import { TRIAL_LESSON_DURATION, isTrialAvailable } from '@shared/lib/trial'
import { differenceInYears } from 'date-fns'
import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useQuery } from '@tanstack/react-query'
import { packages } from '@shared/api/services'

const formatPrice = (value) => Number(value || 0).toLocaleString('ru-RU')

const getExperienceYears = (teacher) => {
  const explicit =
    teacher?.experience_years ||
    teacher?.experienceYears ||
    teacher?.teaching_experience ||
    teacher?.experience

  if (explicit) {
    const years = Number(explicit)
    if (!Number.isNaN(years)) return years
  }

  if (teacher?.teacher_started_educating) {
    return differenceInYears(
      new Date(),
      new Date(teacher.teacher_started_educating)
    )
  }

  return null
}

export const TeacherCard = (teacher) => {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  // Свайп на мобильных: смещение ленты пальцем и флаг перетаскивания
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const isSwipingRef = useRef(false)
  const touchStartXRef = useRef(null)

  const {
    id,
    teacher_slug,
    free_first_lesson,
    first_lesson_discount,
    avatar,
    photos,
    instruments,
    rating,
    reviews_count,
    appointments_count,
    teacher_bio,
    service_description,
    service_info_enabled,
    service_photos,
    nearest_available_time,
    grade,
    trial_available,
    isRecommended = false,
  } = teacher

  const fullName = getFullName(teacher)
  const experienceYears = getExperienceYears(teacher)

  // Сервисные фото показываем строго по флагу service_info_enabled;
  // первое сервисное фото заменяет аватарку
  const hasServicePhotos = !!service_info_enabled && !!service_photos?.length

  const slides = useMemo(() => {
    const list = []
    if (hasServicePhotos) {
      service_photos.forEach((photo) => {
        if (photo?.file) {
          list.push({
            id: photo.id || photo.file,
            src: process.env.REACT_APP_STORAGE_URL + photo.file,
          })
        }
      })
    } else {
      if (avatar?.image) {
        list.push({
          id: 'avatar',
          src: process.env.REACT_APP_STORAGE_URL + avatar.image,
        })
      }
      if (photos?.length) {
        photos.forEach((photo) => {
          if (photo?.file) {
            list.push({
              id: photo.id || photo.file,
              src: process.env.REACT_APP_STORAGE_URL + photo.file,
            })
          }
        })
      }
    }
    if (!list.length) {
      list.push({
        id: 'default',
        src: process.env.REACT_APP_STORAGE_URL + 'images/default_avatar.png',
      })
    }
    return list
  }, [avatar, photos, hasServicePhotos, service_photos])

  const { data: packs } = useQuery({
    queryKey: ['teacher-card-packs', teacher_slug],
    queryFn: () => packages.getTeacherAvaiablePacks(teacher_slug).then((res) => res.data),
    enabled: !!teacher_slug,
    staleTime: 5 * 60 * 1000,
  })

  const packagePerLesson = useMemo(() => {
    if (!packs?.length) return null
    return Math.min(
      ...packs.map((pack) =>
        Math.round(pack.price / (pack.lessons_count || 1))
      )
    )
  }, [packs])

  const maxPackageDiscount = useMemo(() => {
    if (!packs?.length) return null
    return Math.max(...packs.map((pack) => Number(pack.discount || 0)))
  }, [packs])

  const nearestLabel = nearest_available_time
    ? format(
        new TZDate(
          nearest_available_time,
          Intl.DateTimeFormat().resolvedOptions().timeZone
        ),
        'd MMMM в HH:mm',
        { locale: ru }
      )
    : null

  const singlePrice = grade?.price
  const lessonDuration = grade?.duration || 50

  // Триал показываем только если он включён у преподавателя и доступен ученику
  const isTrial = isTrialAvailable(teacher)
  // Скидку на первое занятие не показываем, если триал недоступен (попытка израсходована)
  const showFirstLessonDiscount = first_lesson_discount > 0 && trial_available !== false

  const firstLessonPrice = React.useMemo(() => {
    if (singlePrice == null) return null
    if (free_first_lesson) return 0
    if (showFirstLessonDiscount) {
      return Math.round(singlePrice * (1 - first_lesson_discount / 100))
    }
    return singlePrice
  }, [singlePrice, free_first_lesson, first_lesson_discount, showFirstLessonDiscount])

  const handleCardClick = () => {
    if (isSwipingRef.current) {
      isSwipingRef.current = false
      return
    }
    navigate(`/teacher/${teacher_slug}`)
		window.ym(96194074,'reachGoal','open-teacher-page')
  }

  const handleOpenTeacher = (e) => {
    e.stopPropagation()
    if (isSwipingRef.current) {
      isSwipingRef.current = false
      return
    }
    navigate(`/teacher/${teacher_slug}`)
		window.ym(96194074,'reachGoal','open-teacher-page')
  }

  const handleTouchStart = (e) => {
    isSwipingRef.current = false
    touchStartXRef.current = e.touches[0].clientX
    setDragOffset(0)
    setIsDragging(true)
  }

  const handleTouchMove = (e) => {
    if (touchStartXRef.current === null) return
    let diff = e.touches[0].clientX - touchStartXRef.current
    if (Math.abs(diff) > 5) {
      isSwipingRef.current = true
    }
    // «Резинка» на крайних слайдах: дальше не тянем, а даём сопротивление
    const lastIndex = slides.length - 1
    if ((activeImageIndex === 0 && diff > 0) || (activeImageIndex === lastIndex && diff < 0)) {
      diff = diff / 3
    }
    setDragOffset(diff)
  }

  const handleTouchEnd = (e) => {
    if (touchStartXRef.current === null) return

    const diff = e.changedTouches[0].clientX - touchStartXRef.current
    const minSwipeDistance = 40

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        setActiveImageIndex((prev) => (prev - 1 + slides.length) % slides.length)
      } else {
        setActiveImageIndex((prev) => (prev + 1) % slides.length)
      }
    }

    touchStartXRef.current = null
    setDragOffset(0)
    setIsDragging(false)
  }

  const handleToggleExpand = (e) => {
    e.stopPropagation()
    setIsExpanded((prev) => !prev)
  }

  return (
    <article className={s.card} onClick={handleCardClick}>
      <div className={s.info}>
        <div className={s.photoColumn}>
          <div className={s.photoWrapper} onClick={handleOpenTeacher} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
            <div
            className={classNames(s.track, isDragging && s.dragging)}
            style={{ transform: `translateX(calc(-${activeImageIndex * 100}% + ${dragOffset}px))` }}
          >
            {slides.map((slide) => (
              <img key={slide.id} className={s.photo} src={slide.src} alt={fullName} draggable={false} />
            ))}
          </div>
            {slides.length > 1 && (
              <>
                <div className={s.hoverZones} aria-hidden="true">
                  {slides.map((slide, index) => (
                    <span
                      key={slide.id}
                      onMouseEnter={() => setActiveImageIndex(index)}
                    />
                  ))}
                </div>
                <div className={s.sliderDots} aria-hidden="true" onClick={(e) => e.stopPropagation()}>
                  {slides.map((slide, index) => (
                    <span
                      key={slide.id}
                      className={index === activeImageIndex ? s.activeDot : ''}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className={s.contentColumn}>
          {isRecommended && (
            <span className={s.recommendedBadge}>
              <IconSparkles size={14} stroke={2} />
              Рекомендуем
            </span>
          )}
          <h3 className={s.name} onClick={handleOpenTeacher}>
            {fullName}
          </h3>

          <div className={s.badges}>
            <div className={s.badgeRow}>
              {instruments?.map((instrument) => (
                <span key={instrument.id} className={s.badge}>
                  <TextWithEmoji text={instrument.name} />
                </span>
              ))}
              {experienceYears > 0 && (
                <span className={s.badge}>
                  <IconStar className={s.badgeIcon} size={14} />
                  {experienceYears} {getNoun(experienceYears, 'год', 'года', 'лет')} опыта
                </span>
              )}
              {isTrial ? (
                <span className={s.badge}>
                  <IconSparkles className={s.badgeIcon} size={14} />
                  Пробное занятие
                </span>
              ) : (
                <>
              {free_first_lesson && (
                <span className={s.badge}>
                  <IconStar className={s.badgeIcon} size={14} />
                  Бесплатное первое занятие
                </span>
              )}
              {!free_first_lesson && showFirstLessonDiscount && (
                <span className={s.badge}>
                  <IconPercentage className={s.badgeIcon} size={14} />
                  -{first_lesson_discount}% на первое занятие
                </span>
              )}
                </>
              )}
            </div>
          </div>

          <div className={s.regals}>
            {rating != null && (
              <div className={s.regal}>
                <IconStarFilled size={18} />
                <span>{rating}</span>
                {reviews_count != null && reviews_count > 0 && (
                  <>
                    <span>•</span>
                    <span>
                      {reviews_count} {getNoun(reviews_count, 'отзыв', 'отзыва', 'отзывов')}
                    </span>
                  </>
                )}
              </div>
            )}
            {appointments_count != null && (
              <div className={s.regal}>
                <IconBook size={18} />
                <span>
                  {appointments_count} {getNoun(appointments_count, 'урок', 'урока', 'уроков')}
                </span>
              </div>
            )}
          </div>

          {(service_description || teacher_bio) && (
            <div className={s.description}>
              <p className={`${s.descriptionText} ${!isExpanded ? s.collapsed : ''}`}>
                {service_description && (
                  <span className={s.serviceDescription}>{service_description}</span>
                )}
                {service_description && teacher_bio && ' - '}
                {teacher_bio}
              </p>
              <button
                type="button"
                className={s.readMore}
                onClick={handleToggleExpand}
              >
                {isExpanded ? 'Свернуть' : 'Читать далее'}
              </button>
            </div>
          )}
        </div>

        <div className={s.actionsColumn} onClick={(e) => e.stopPropagation()}>
          <div className={s.priceRow}>
            {isTrial ? (
              <div className={s.priceBlock}>
                <div className={`${s.priceValue} ${s.accent}`}>
                  <span>Бесплатно</span>
                </div>
                <span className={`${s.priceLabel} ${s.accentLabel}`}>
                  пробное занятие • {TRIAL_LESSON_DURATION} мин.
                </span>
                {singlePrice != null && (
                  <span className={s.trialNextLabel}>
                    далее от {formatPrice(singlePrice)} ₽/{lessonDuration} мин
                  </span>
                )}
              </div>
            ) : (
              <>
            {singlePrice != null && (
              <div className={s.priceBlock}>
                {free_first_lesson ? (
                  <>
                    <div className={`${s.priceValue} ${s.accent}`}>
                      <span>Бесплатно</span>
                    </div>
                    <span className={`${s.priceLabel} ${s.accentLabel}`}>первое занятие</span>
                  </>
                ) : (
                  <>
                    <div className={s.priceValue}>
                      <span className={s.pricePrefix}>от</span>
                      <span>{formatPrice(firstLessonPrice)} ₽</span>
                    </div>
                    {showFirstLessonDiscount ? (
                      <span className={s.priceLabel}>
                        <span className={s.oldPrice}>{formatPrice(singlePrice)} ₽</span>
                        {' '}за {lessonDuration} мин.
                      </span>
                    ) : (
                      <span className={s.priceLabel}>за {lessonDuration} мин.</span>
                    )}
                  </>
                )}
              </div>
            )}
            {packagePerLesson != null && !isTrial && (
              <div className={s.priceBlock}>
                <div className={`${s.priceValue} ${s.accent}`}>
                  <span className={s.pricePrefix}>от</span>
                  <span>{formatPrice(packagePerLesson)} ₽</span>
                </div>
                <span className={`${s.priceLabel} ${s.accentLabel}`}>в пакете</span>
              </div>
            )}
              </>
            )}
          </div>

          <div className={s.metaRow}>
            {nearestLabel && (
              <div className={s.metaItem}>
                <IconClockHour3 size={14} />
                <span>
                  Ближайшая запись <strong>{nearestLabel}</strong>
                </span>
              </div>
            )}
            {maxPackageDiscount > 0 && !isTrial && (
              <div className={s.metaItem}>
                <IconDiscount size={14} />
                <span>
                  Экономьте до {maxPackageDiscount}% на пакетах
                </span>
              </div>
            )}
          </div>

          <div className={s.buttonList}>
            <div onClick={(e) => e.stopPropagation()}>
              <Button
                to={`/teacher/booking/${teacher_slug}`}
                label="Записаться"
                className={s.bookButton}
              />
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <StartChatButton teacherId={id} className={s.detailsButton} />
            </div>
          </div>
        </div>
      </div>
    </article>
  )
}
